import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FetchReportRequest, FetchReportResponse } from 'src/models/reports/FetchReport';
import ErrorUtils from 'src/utils/ErrorUtils';
import { fetchSegmentCliData, SegmentCliData, segmentCategoryFromHeader } from 'src/components/reports/cliProvenance';

/**
 * Everything the TP P&L grid needs to fetch one functional segment on demand. Supplied by the report
 * view only while drill-in is allowed (flag on, Functional Segment = "All"); null otherwise.
 */
export interface SegmentRequestContext {
    /**
     * Identity of the base report (entity / period / day / property filters, NOT pagination). Changing
     * it clears every expansion and cache and discards in-flight responses; paging does not.
     */
    contextKey: string;
    /** The base report's request with functionalSegmentCategory and paginationConfig replaced. */
    buildRequest: (segmentCategory: string, pageNumber: number, pageSize: number) => FetchReportRequest;
}

export interface SegmentExpansionStatus {
    expanded: boolean;
    loading: boolean;
    error?: string;
    /** Known once the segment has been fetched at least once. */
    cliCount?: number;
    truncated?: boolean;
}

export interface SegmentCliExpansion {
    enabled: boolean;
    /** Only segments that are expanded AND loaded; drives the column model. */
    expandedClisBySegment: { [segmentHeader: string]: string[] };
    /** Same segments, with values and captions, for the row merge and CLI headers. */
    expandedSegmentData: SegmentCliData[];
    statusOf: (segmentHeader: string) => SegmentExpansionStatus;
    toggleSegment: (segmentHeader: string) => void;
}

const without = <T,>(record: { [key: string]: T }, key: string): { [key: string]: T } => {
    if (!(key in record)) {
        return record;
    }
    const copy = { ...record };
    delete copy[key];
    return copy;
};

/**
 * Expand/collapse state for LEF segment-total columns (Story 3.1). Segments toggle independently;
 * each is fetched once per report context and then served from cache, so collapse + re-expand is
 * instant. A failed fetch collapses the segment and records the error; toggling again retries.
 */
export function useSegmentCliExpansion(options: {
    context: SegmentRequestContext | null | undefined;
    fetchPage: (request: FetchReportRequest) => Promise<FetchReportResponse>;
    knownCategories?: ReadonlyArray<string> | null;
}): SegmentCliExpansion {
    const { context, fetchPage, knownCategories } = options;
    const enabled = !!context;
    const contextKey = context?.contextKey ?? null;

    const [expanded, setExpanded] = useState<string[]>([]);
    const [cache, setCache] = useState<{ [segmentHeader: string]: SegmentCliData }>({});
    const [loading, setLoading] = useState<{ [segmentHeader: string]: true }>({});
    const [errors, setErrors] = useState<{ [segmentHeader: string]: string }>({});

    // Bumped on every context change and on unmount; a response from an older generation is dropped,
    // so a slow fetch for the previous entity can never land in the current report.
    const generation = useRef(0);
    // Latest inputs for the async callback, without re-creating toggleSegment on every render.
    const latest = useRef({ context, fetchPage, knownCategories, expanded, cache, loading });
    latest.current = { context, fetchPage, knownCategories, expanded, cache, loading };

    useEffect(() => {
        setExpanded([]);
        setCache({});
        setLoading({});
        setErrors({});
        return () => {
            generation.current += 1;
        };
    }, [contextKey]);

    const toggleSegment = useCallback((segmentHeader: string) => {
        const current = latest.current;
        if (!current.context) {
            return;
        }
        if (current.expanded.includes(segmentHeader)) {
            setExpanded((previous) => previous.filter((header) => header !== segmentHeader));
            return;
        }

        setErrors((previous) => without(previous, segmentHeader));
        setExpanded((previous) => (previous.includes(segmentHeader) ? previous : [...previous, segmentHeader]));
        if (current.cache[segmentHeader] || current.loading[segmentHeader]) {
            return;
        }

        const requestGeneration = generation.current;
        const isCurrent = () => requestGeneration === generation.current;
        const { buildRequest } = current.context;
        const category = segmentCategoryFromHeader(segmentHeader, current.knownCategories);

        setLoading((previous) => ({ ...previous, [segmentHeader]: true }));
        fetchSegmentCliData(
            segmentHeader,
            (pageNumber, pageSize) => buildRequest(category, pageNumber, pageSize),
            current.fetchPage
        )
            .then((data) => {
                if (isCurrent()) {
                    setCache((previous) => ({ ...previous, [segmentHeader]: data }));
                }
            })
            .catch((error) => {
                if (isCurrent()) {
                    setErrors((previous) => ({ ...previous, [segmentHeader]: ErrorUtils.getMessage(error) }));
                    setExpanded((previous) => previous.filter((header) => header !== segmentHeader));
                }
            })
            .finally(() => {
                if (isCurrent()) {
                    setLoading((previous) => without(previous, segmentHeader));
                }
            });
    }, []);

    const expandedSegmentData = useMemo(
        () => (enabled ? expanded.map((header) => cache[header]).filter((data): data is SegmentCliData => !!data) : []),
        [enabled, expanded, cache]
    );

    const expandedClisBySegment = useMemo(() => {
        const bySegment: { [segmentHeader: string]: string[] } = {};
        expandedSegmentData.forEach((data) => {
            bySegment[data.segmentHeader] = data.clis;
        });
        return bySegment;
    }, [expandedSegmentData]);

    const statusOf = useCallback(
        (segmentHeader: string): SegmentExpansionStatus => ({
            expanded: enabled && expanded.includes(segmentHeader),
            loading: !!loading[segmentHeader],
            error: errors[segmentHeader],
            cliCount: cache[segmentHeader]?.clis.length,
            truncated: cache[segmentHeader]?.truncated,
        }),
        [enabled, expanded, loading, errors, cache]
    );

    return { enabled, expandedClisBySegment, expandedSegmentData, statusOf, toggleSegment };
}
